/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useRef, useEffect } from "react";
import {
  FiGlobe,
  FiMoon,
  FiSun,
  FiUser,
  FiChevronDown,
  FiLogOut,
  FiMenu,
  FiSettings,
  FiAward,
  FiClock,
} from "react-icons/fi";
import { useUI } from "../theme/ThemeProvider";
import { useNavigate } from "react-router-dom";
import { useStore } from "../../context/store/StoreContext";
import { useAuth } from "../../context/auth/AuthContext";
import {
  fetchCurrentTenantPlan,
  getPlanDisplayName,
  getRemainingSubscriptionDays,
  invalidateTenantPlanCache,
  isPlanExpired,
} from "../utils/subscriptionPlan";
import { getStoreDisplayName } from "../utils/getStoreDisplayName";
import { goToLoginScreen } from "../../navigation/limitedBackHistory";

const COLORS = {
  purple: "#7400BF",
};

export default function Navbar({ onToggleSidebar, isSidebarOpen }) {
  const { theme, toggleTheme, lang, toggleLang } = useUI();
  const isDark = theme === "dark";
  const isArabic = lang === "ar";
  const navigate = useNavigate();
  const { clearStoreData } = useStore();

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // حالات الأسماء
  const [userName, setUserName] = useState("");
  const [storeName, setStoreName] = useState("");
  const [planName, setPlanName] = useState("");
  const [remainingDays, setRemainingDays] = useState(null);
  const [planExpired, setPlanExpired] = useState(false);

  const [profileImage, setProfileImage] = useState(localStorage.getItem("profileImage"));
  // ... داخل مكون Navbar
  useEffect(() => {
    const syncData = () => {
      // 1. جلب اسم المستخدم (التاجر) من TenantName
      // 1. جلب اسم المستخدم (التاجر) من StoreData (TenantName أو EngTenantName)
      let storeData = {};
      try {
        storeData = JSON.parse(localStorage.getItem("storeData")) || {};
      } catch (e) {
        storeData = {};
      }

      const tenantNameFromStore = isArabic
        ? (storeData.TenantName || storeData.EngTenantName)
        : (storeData.EngTenantName || storeData.TenantName);

      const uName = tenantNameFromStore || localStorage.getItem("tenantName") || (isArabic ? "المستخدم" : "User");
      const nextStoreName = getStoreDisplayName(isArabic);

      const img = localStorage.getItem("profileImage");

      if (uName !== userName) setUserName(uName);
      if (nextStoreName !== storeName) setStoreName(nextStoreName);
      if (img !== profileImage) setProfileImage(img);
    };

    syncData();
    const interval = setInterval(syncData, 1000);

    return () => clearInterval(interval);
  }, [isArabic, userName, storeName, profileImage]);

  useEffect(() => {
    let cancelled = false;

    const loadPlan = async () => {
      try {
        const plan = await fetchCurrentTenantPlan();
        if (cancelled) return;
        if (!plan) {
          setPlanName("");
          setRemainingDays(null);
          setPlanExpired(false);
          return;
        }
        setPlanName(getPlanDisplayName(plan, isArabic) || (isArabic ? "باقة" : "Plan"));
        setRemainingDays(getRemainingSubscriptionDays(plan));
        setPlanExpired(isPlanExpired(plan));
      } catch {
        if (!cancelled) {
          setPlanName("");
          setRemainingDays(null);
          setPlanExpired(false);
        }
      }
    };

    loadPlan();
    window.addEventListener("focus", loadPlan);
    const interval = setInterval(loadPlan, 60 * 1000);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", loadPlan);
      clearInterval(interval);
    };
  }, [isArabic]);


  /* ----------------------------------------------------------------
     Logout Logic
  ---------------------------------------------------------------- */
  const { logout } = useAuth(); // authentication hook

  const handleLogout = () => {
    // 1. Clear Context Data
    clearStoreData();
    logout();
    invalidateTenantPlanCache();

    // 2. Clear LocalStorage keys managed by useLogin, BUT Keep Preferences (lang, theme)
    const keysToRemove = [
      "fullLoginResponse", "token", "authToken", "authRole", "user", "isTenantOwner",
      "loginMessage", "tenant", "tenantDetails",
      "plans", "themes", "paymentMethods", "tenantUsersList", "tenantId",
      "companyStoreSlug", "companyStoreName", "companySector", "companyServices",
      "companyPaymentMethods", "storeData",       "permissions", "subscriptionStartDate", "paidStartDate", "trialStartDate",
      "subscriptionDurationDays", "plan_expiry_date", "plan_renewed_at",
      "trialUsed", "force_expiry_notice", "plan_duration_type",
      "subscription", "subscriptionExpired", "isTrial",
    ];
    keysToRemove.forEach(k => localStorage.removeItem(k));

    // 3. Close Dropdown & Redirect to the new login screen
    setDropdownOpen(false);
    goToLoginScreen(navigate);
  };

  // إغلاق القائمة المنسدلة عند النقر في الخارج
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const daysLeft = typeof remainingDays === "number" ? remainingDays : null;
  const daysCritical = planExpired || (daysLeft != null && daysLeft <= 3);
  const daysWarning = !daysCritical && daysLeft != null && daysLeft <= 7;
  const daysAccent = daysCritical
    ? "#ef4444"
    : daysWarning
      ? "#d97706"
      : isDark ? "#F2B441" : "#059669";
  const daysAccentBg = daysCritical
    ? "rgba(239,68,68,0.14)"
    : daysWarning
      ? "rgba(245,158,11,0.16)"
      : isDark ? "rgba(242,180,65,0.16)" : "rgba(34,197,94,0.12)";

  return (
    <header
      dir={isArabic ? "rtl" : "ltr"}
      className={`sticky top-0 z-[1000] flex items-center justify-between w-full h-[72px] lg:h-[84px] px-4 sm:px-6 backdrop-blur-md
        ${isDark
          ? "bg-[#0F172A]/90 text-gray-100 border-b border-[#1E293B]"
          : "bg-white/90 text-gray-800 border-b border-gray-200"
        }`}
    >
      {/* الجزء الأيسر: زر القائمة واسم المتجر وباقة الاشتراك */}
      <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
        <button
          onClick={() => onToggleSidebar(!isSidebarOpen)}
          className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-xl border transition-all active:scale-95 shrink-0
            ${isDark
              ? "border-[#334155] bg-[#1E293B]/50  text-gray-200"
              : "border-gray-200 bg-gray-50  text-gray-700"
            }`}
        >
          <FiMenu size={20} />
        </button>

        {/* اسم المتجر يظهر فقط في الشاشات الصغيرة لأن السايدبار مخفي */}
        <div className={`${planName ? "hidden md:flex" : "flex"} lg:hidden flex-col leading-tight min-w-0`}>
          <span className={`text-sm font-bold truncate max-w-[88px] sm:max-w-[140px] ${isDark ? "text-white" : "text-[#7400BF]"}`}>
            {storeName || userName || (isArabic ? "مدار" : "Madar")}
          </span>
        </div>

        {planName ? (
          <div className="flex items-stretch gap-1.5 md:gap-3 lg:gap-4 min-w-0 flex-1 md:flex-none overflow-hidden">
            <button
              type="button"
              onClick={() => navigate("/admin-store/subscriptions")}
              className={`h-11 md:h-14 lg:h-16 px-2 md:px-4 lg:px-5 rounded-xl md:rounded-2xl border flex items-center gap-2 md:gap-3 min-w-0 flex-1 basis-0 justify-center md:justify-start md:flex-none md:basis-auto md:max-w-[240px] lg:max-w-[300px] transition-all active:scale-95
                ${isDark
                  ? "border-[#334155] bg-[#1E293B]/50"
                  : "border-gray-200 bg-gray-50"}`}
              title={isArabic ? "الباقة المستخدمة" : "Current plan"}
            >
              <span
                className="hidden md:flex w-9 h-9 lg:w-11 lg:h-11 rounded-xl items-center justify-center shrink-0"
                style={{ backgroundColor: isDark ? "rgba(116,0,191,0.22)" : "#F3E8FF" }}
              >
                <FiAward className="text-[#6D28D9] w-4 h-4 lg:w-[18px] lg:h-[18px]" />
              </span>
              <span className="flex flex-col min-w-0 leading-tight text-center md:text-start">
                <span className="text-[10px] lg:text-xs font-bold opacity-60">
                  {isArabic ? "الباقة" : "Plan"}
                </span>
                <span className="text-sm lg:text-base font-bold truncate text-[#6D28D9] mt-0.5">
                  {planName}
                </span>
              </span>
            </button>

            <button
              type="button"
              onClick={() => navigate("/admin-store/subscriptions")}
              className={`h-11 md:h-14 lg:h-16 px-2 md:px-4 lg:px-5 rounded-xl md:rounded-2xl border flex items-center gap-2 md:gap-3 min-w-0 flex-1 basis-0 justify-center md:justify-start md:flex-none md:basis-auto md:shrink-0 transition-all active:scale-95
                ${isDark
                  ? "border-[#334155] bg-[#1E293B]/50"
                  : "border-gray-200 bg-gray-50"}`}
              title={isArabic ? "الأيام المتبقية للاشتراك" : "Days remaining"}
            >
              <span
                className="hidden md:flex w-9 h-9 lg:w-11 lg:h-11 rounded-xl items-center justify-center shrink-0"
                style={{ backgroundColor: daysAccentBg }}
              >
                <FiClock className="w-4 h-4 lg:w-[18px] lg:h-[18px]" style={{ color: daysAccent }} />
              </span>
              <span className="flex flex-col min-w-0 leading-tight text-center md:text-start">
                <span className="text-[10px] lg:text-xs font-bold opacity-60">
                  {isArabic ? "متبقي" : "Left"}
                </span>
                <span
                  className="text-sm lg:text-base font-bold truncate md:whitespace-nowrap mt-0.5"
                  style={{ color: daysAccent }}
                >
                  {planExpired
                    ? (isArabic ? "منتهي" : "Expired")
                    : remainingDays == null
                      ? "—"
                      : isArabic
                        ? `${remainingDays} يوم`
                        : `${remainingDays} ${remainingDays === 1 ? "day" : "days"}`}
                </span>
              </span>
            </button>
          </div>
        ) : null}
      </div>

      {/* الجزء الأيمن: اللغة، الثيم، والملف الشخصي */}
      <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
        {/* زر اللغة */}
        <button
          onClick={toggleLang}
          className={`h-9 sm:h-10 px-2 sm:px-3 flex items-center gap-1.5 sm:gap-2 rounded-xl border transition-all active:scale-95
            ${isDark
              ? "border-[#334155] bg-[#1E293B]/50  text-gray-200"
              : "border-gray-200 bg-gray-50  text-gray-700"}
          `}
        >
          <FiGlobe className="text-[#6D28D9]" size={16} />
          <span className="text-[10px] sm:text-sm font-bold uppercase select-none">
            {isArabic ? "En" : "Ar"}
          </span>
        </button>

        {/* زر الثيم */}
        <button
          onClick={toggleTheme}
          className={`w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-xl border transition-all active:scale-95
            ${isDark
              ? "border-[#334155] bg-[#1E293B]/50 "
              : "border-gray-200 bg-gray-50 "}
          `}
        >
          {isDark ? <FiSun className="text-[#F2B441]" size={17} /> : <FiMoon className="text-[#6D28D9]" size={17} />}
        </button>

        {/* الملف الشخصي للمستخدم */}
        <div ref={dropdownRef} className="relative">
          <button
            onClick={() => setDropdownOpen((p) => !p)}
            className={`flex items-center gap-2 px-1.5 sm:px-3 py-1.5 rounded-xl border transition-all active:scale-95
              ${isDark
                ? "border-[#334155] bg-[#1E293B]/50 "
                : "border-gray-200 bg-gray-50 "}
            `}
          >
            {profileImage ? (
              <img
                src={profileImage}
                alt="user"
                className="w-7 h-7 sm:w-8 sm:h-8 rounded-full object-cover border border-purple-500/20"
              />
            ) : (
              <div
                className="w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center"
                style={{ backgroundColor: isDark ? "#1E293B" : "#F3E8FF" }}
              >
                <FiUser size={16} style={{ color: COLORS.purple }} />
              </div>
            )}

            {/* عرض اسم المستخدم مختفي في الموبايل لتقليل الزحمة */}
            <span className="hidden md:block font-bold text-sm text-[#6D28D9] max-w-[100px] truncate">
              {userName || (isArabic ? "المالك" : "Owner")}
            </span>

            <FiChevronDown
              size={14}
              className={`text-[#6D28D9] transition-transform duration-300 ${dropdownOpen ? "rotate-180" : ""}`}
            />
          </button>

          {/* القائمة المنسدلة */}
          {dropdownOpen && (
            <div
              className={`absolute top-[calc(100%+10px)] ${isArabic ? "left-0" : "right-0"} 
                min-w-[220px] rounded-2xl overflow-hidden shadow-2xl border animate-in fade-in slide-in-from-top-2
                ${isDark ? "bg-[#1E293B] border-[#334155]" : "bg-white border-gray-100"}`}
              style={{ zIndex: 9999 }}
            >
              <div className="px-4 py-3 border-b border-gray-100 dark:border-[#334155] md:hidden">
                <p className="text-xs text-gray-400 mb-0.5">{isArabic ? "أهلاً بك" : "Welcome"}</p>
                <p className="text-sm font-bold text-[#6D28D9] truncate">{userName || (isArabic ? "المالك" : "Owner")}</p>
              </div>

              <button
                onClick={() => { setDropdownOpen(false); navigate("/admin-store/profile-settings"); }}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition
                  ${isDark ? "text-gray-200" : "text-gray-700"}`}
              >
                <FiSettings className="text-[#6D28D9]" size={16} />
                <span>{isArabic ? "إعدادات الملف الشخصي" : "Profile Settings"}</span>
              </button>

              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-500  transition"
              >
                <FiLogOut size={16} />
                <span className="font-bold">{isArabic ? "تسجيل الخروج" : "Logout"}</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}